// #region: Class definition *********************************************************************/
/**
 * @class questionController
 * @description Abstract base class for question-type controllers.
 * Provides comprehensive infrastructure for managing question-specific data, UI interactions,
 * and cross-component communication in form contexts.
 * 
 * @abstract
 * 
 * @example
 * // Extending the base class for a custom question type.
 * class MyquestionController extends questionController {
 *     constructor() {
 *         super();
 *         this.setupMyQuestionHandlers();
 *     }
 * 
 *     getControllerType() {
 *         return 'myquestion';
 *     }
 * 
 *     initialize(options = {}) {
 *         super.initialize(options);
 * 
 *         // Custom initialization logic here.
 *     }
 * 
 *     async onAfterFormSave(oldFormId, newFormId, savedData) {
 *         // Handle post-save updates specific to this question type.
 *         // Called automatically by questionControllerRegistry after form saves.
 *     }
 * }
 * 
 * @example
 * // Using singleton pattern (recommended).
 * window.myquestionController = window.myquestionController || new MyquestionController();
 * 
 * // Initialize for a specific form.
 * myquestionController.initialize({ formId: 123, questionId: 456 });
 * 
 * // Register message handlers.
 * myquestionController.registerMessageHandler('customAction', (data) => {
 *     console.log('Received custom action:', data);
 * });
 * 
 * @remarks
 * Core Features:
 * - **Form Association**: Each controller is bound to a single form ID throughout its lifecycle.
 * - **Data Caching**: Efficient key-value storage for question-specific data with cache management.
 * - **DOM Utilities**: Simple DOM element query helpers for derived classes.
 * - **Event Management**: Comprehensive event listener tracking with automatic cleanup.
 * - **Message Handling**: Cross-window communication with type-safe message routing.
 * - **Registry Integration**: Automatic registration with the global controller registry.
 * - **Form Save Notifications**: Optional onAfterFormSave() method for post-save updates.
 * 
 * Architecture Principles:
 * - One controller instance per question type (singleton pattern recommended).
 * - Lazy initialization with cache preservation across dialog operations.
 * - Clean separation of public API and private implementation.
 * - Memory-safe with automatic cleanup on controller destruction.
 * 
 * Cache Strategy:
 * - Data cache persists across initialize() calls unless explicitly cleared.
 * - Form ID change triggers automatic cache clearing for data consistency.
 * 
 * Events Handled:
 * - Window messages for cross-dialog communication.
 * - DOM events through tracked event listeners.
 * - Custom controller-specific events via message handlers.
 * - Form save notifications via onAfterFormSave() method.
 * 
 * Form Save Integration:
 * - Controllers can implement onAfterFormSave(oldFormId, newFormId, savedData) method.
 * - Method is called automatically by questionControllerRegistry after form saves.
 * - Supports both synchronous and asynchronous implementations.
 * - Receives complete server response data for maximum flexibility.
 */
class questionController {

    // #region: Private Fields ***********************************************************************/
    /**
     * ID of the currently active form.
     * Set during initialization and reused throughout the controller's lifecycle.
     * @private
     * @type {number|string|null}
     * 
     * @memberof questionController
     */
    #activeFormId = null;

    /**
     * Bound window message handler for cleanup purposes.
     * @private
     * @type {Function|null}
     * 
     * @memberof questionController
     */
    #boundWindowMessageHandler = null;

    /**
     * Cache for question-specific data.
     * Uses string keys to store arbitrary data associated with questions, forms, or other entities.
     * @private
     * @type {Map<string, any>}
     * 
     * @memberof questionController
     */
    #dataCache = new Map();

    /**
     * Array of event listeners managed by this controller.
     * Tracks all event listeners for automatic cleanup on controller destruction.
     * @private
     * @type {Array<{element: Element|Window|Document, event: string, handler: Function, options?: any}>}
     * 
     * @memberof questionController
     */
    #eventListeners = [];

    /**
     * Map of registered message handlers for window messages.
     * Enables type-safe cross-window communication with automatic routing.
     * @private
     * @type {Map<string, Function>}
     * 
     * @memberof questionController
     */
    #messageHandlers = new Map();
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * Creates an instance of questionController.
     * Initializes core infrastructure including controller registry registration and base event handler setup.
     * 
     * @memberof questionController
     * @throws {Error} If getControllerType() is not implemented by derived class.
     */
    constructor() {
        // Register with global controller registry if available.
        if (window.questionControllerRegistry) {
            window.questionControllerRegistry.register(this.getControllerType(), this);
        }

        // Setup base event handlers.
        this.#setupBaseEventHandlers();
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Identifies class.
     * @returns {String} The string representation of this class.
     */
    toString() {
        return "[questionController]";
    }

    static toString() {
        return "[questionController]";
    }

    /** 
     * Identifies the version.
     * @returns {String} The version number of this class.
     */
    static version() {
        return "1.0.0";
    }

    /** 
     * Identifies the version (instance method).
     * @returns {String} The version number of this class.
     */
    version() {
        return "1.0.0";
    }
    // #endregion ********************************************************************************/

    // #region: Abstract Methods (Must be overridden) *******************************************/
    /**
     * Gets the unique controller type identifier.
     * This identifier is used for controller registry and message routing.
     * @abstract
     * @returns {string} The controller type identifier (e.g., 'inspectionpoint', 'matrix').
     * @throws {Error} If not implemented by derived class.
     * 
     * @memberof questionController
     */
    getControllerType() {
        throw new Error('getControllerType() must be implemented by derived class');
    }
    // #endregion ********************************************************************************/

    // #region: Public Methods *******************************************************************/
    /**
     * Clears all cached data.
     * Can be overridden by derived classes for additional cleanup.
     * Does not clear form ID or event listeners - use destroy() for complete cleanup.
     * 
     * @memberof questionController
     */
    clearCache() {
        this.#dataCache.clear();
    }

    /**
     * Cleans up the controller when it's no longer needed.
     * Removes all event listeners, clears caches, and unregisters from global registry.
     * Should be called when the form is closed or navigated away from.
     * 
     * @memberof questionController
     */
    destroy() {
        // Remove all event listeners.
        this.removeAllEventListeners();
        
        // Clear all caches.
        this.clearCache();
        
        // Clear message handlers.
        this.#messageHandlers.clear();
        
        // Unregister from global registry if available.
        if (window.questionControllerRegistry) {
            window.questionControllerRegistry.unregister(this.getControllerType());
        }
    }

    /**
     * Gets the currently active form ID.
     * @returns {number|string|null} The active form ID, or null if not initialized.
     * 
     * @memberof questionController
     */
    getFormId() {
        return this.#activeFormId;
    }

    /**
     * Initializes the controller with form and question context.
     * Should be overridden by derived classes but must call super.initialize().
     * Cache is preserved by default unless explicitly cleared or form ID changes.
     * @param {Object} options - Initialization options.
     * @param {number|string} options.formId - The form ID to associate with this controller.
     * @param {boolean} [options.clearCache=false] - Whether to clear existing cache.
     * 
     * @memberof questionController
     */
    initialize(options = {}) {
        if (options.formId) {
            this.#activeFormId = options.formId;
        }

        // Clear cache if explicitly requested or form ID changed.
        if (options.clearCache === true) {
            this.clearCache();
        }
    }

    /**
     * Sets the form ID and optionally clears cache.
     * Used when form ID is updated after form save operations.
     * @param {number|string} formId - The new form ID.
     * @param {boolean} [clearCache=false] - Whether to clear cache on form ID change.
     * 
     * @memberof questionController
     */
    setFormId(formId, clearCache = false) {
        this.#activeFormId = formId;

        if (clearCache) {
            this.clearCache();
        }
    }
    // #endregion ********************************************************************************/

    // #region: Public Data Cache Methods (for derived classes) ************************************/
    /**
     * Creates a cache key from multiple parts.
     * Provides consistent key generation for cache operations.
     * @param {...any} parts - The parts to combine into a key (will be joined with '-').
     * @returns {string} The generated cache key.
     * 
     * @memberof questionController
     */
    createCacheKey(...parts) {
        return parts.join('-');
    }

    /**
     * Removes data from cache.
     * @param {string} key - The cache key to remove.
     * @returns {boolean} True if the key existed and was deleted, false otherwise.
     * 
     * @memberof questionController
     */
    deleteData(key) {
        return this.#dataCache.delete(key);
    }

    /**
     * Gets data from cache.
     * @param {string} key - The cache key to retrieve.
     * @returns {any} The cached data, or undefined if key doesn't exist.
     * 
     * @memberof questionController
     */
    getData(key) {
        return this.#dataCache.get(key);
    }

    /**
     * Checks if data exists in cache.
     * @param {string} key - The cache key to check.
     * @returns {boolean} True if the key exists in cache.
     * 
     * @memberof questionController
     */
    hasData(key) {
        return this.#dataCache.has(key);
    }

    /**
     * Sets data in cache.
     * @param {string} key - The cache key to store under.
     * @param {any} value - The value to cache.
     * 
     * @memberof questionController
     */
    setData(key, value) {
        this.#dataCache.set(key, value);
    }
    // #endregion ********************************************************************************/

    // #region: Public Event Handling Methods (for derived classes) ********************************/
    /**
     * Adds an event listener and tracks it for automatic cleanup.
     * All listeners added through this method will be automatically removed when destroy() is called.
     * @param {Element|Window|Document} element - The element to attach the listener to.
     * @param {string} event - The event name (e.g., 'click', 'change', 'message').
     * @param {Function} handler - The event handler function.
     * @param {Object} [options] - Optional event listener options (passive, once, etc.).
     * 
     * @memberof questionController
     */
    addEventListener(element, event, handler, options) {
        element.addEventListener(event, handler, options);
        this.#eventListeners.push({ element, event, handler, options });
    }

    /**
     * Registers a message handler for window messages.
     * Enables type-safe cross-window communication with automatic message routing.
     * @param {string} action - The action or message type to handle.
     * @param {Function} handler - The handler function to call when message is received.
     * 
     * @memberof questionController
     */
    registerMessageHandler(action, handler) {
        this.#messageHandlers.set(action, handler);
    }

    /**
     * Removes a specific event listener and stops tracking it.
     * Usually not needed - destroy() removes all automatically.
     * @param {Element|Window|Document} element - The element to remove the listener from.
     * @param {string} event - The event name.
     * @param {Function} handler - The event handler function.
     * 
     * @memberof questionController
     */
    removeEventListener(element, event, handler) {
        element.removeEventListener(event, handler);
        
        // Remove from tracking.
        const index = this.#eventListeners.findIndex((listener) => {
            return listener.element === element && 
                   listener.event === event && 
                   listener.handler === handler;
        });
        
        if (index > -1) {
            this.#eventListeners.splice(index, 1);
        }
    }

    /**
     * Removes all tracked event listeners.
     * Called automatically by destroy() - rarely needs to be called directly.
     * 
     * @memberof questionController
     */
    removeAllEventListeners() {
        this.#eventListeners.forEach(({ element, event, handler, options }) => {
            element.removeEventListener(event, handler, options);
        });
        
        this.#eventListeners = [];
    }

    /**
     * Sends a message to a target window.
     * Provides safe cross-window communication with origin validation.
     * @param {Window} targetWindow - The target window to send message to.
     * @param {Object} message - The message object to send.
     * @param {string} [targetOrigin=window.origin] - The target origin for security.
     * 
     * @memberof questionController
     */
    sendMessage(targetWindow, message, targetOrigin = window.origin) {
        if (targetWindow && targetWindow !== window) {
            targetWindow.postMessage(message, targetOrigin);
        }
    }
    // #endregion ********************************************************************************/

    // #region: Private Methods ******************************************************************/
    /**
     * Handles window message events.
     * Validates message origin and routes to registered handlers based on action/type.
     * @private
     * @param {MessageEvent} event - The message event.
     * 
     * @memberof questionController
     */
    #handleWindowMessage(event) {
        // Validate message origin for security.
        if (event.origin !== window.origin || !event.data) {
            return;
        }

        // Check for registered handlers.
        const action = event.data.action || event.data.type;
        
        if (action && this.#messageHandlers.has(action)) {
            const handler = this.#messageHandlers.get(action);

            handler.call(this, event.data);
        }
    }

    /**
     * Sets up base event handlers common to all controllers.
     * Initializes window message handling with proper binding.
     * @private
     * 
     * @memberof questionController
     */
    #setupBaseEventHandlers() {
        // Bind the window message handler for cleanup tracking.
        this.#boundWindowMessageHandler = this.#handleWindowMessage.bind(this);
        this.addEventListener(window, 'message', this.#boundWindowMessageHandler);
    }
    // #endregion ********************************************************************************/
}
// #endregion ************************************************************************************/